import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";

const KEY = "evoo_cookie_consent";

const PrefRow = ({ title, desc, value, onChange, locked, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <p className="text-sm font-medium text-neutral-800">{title}</p>
      <p className="text-xs text-neutral-500">{desc}</p>
    </div>
    <Switch checked={!!value} disabled={locked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const CookieConsent = () => {
  const { t } = useApp();
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem(KEY); } catch { return false; }
  });
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  if (!visible) return null;

  const persist = (choice) => {
    try { localStorage.setItem(KEY, JSON.stringify({ essential: true, ...choice, ts: Date.now() })); } catch (e) { /* noop */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
      data-testid="cookie-consent"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto max-h-[85vh] max-w-3xl overflow-y-auto rounded-xl border border-border bg-white p-4 shadow-2xl sm:p-5">
        <div className="flex items-start gap-3">
          <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:flex">
            <Cookie className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-serif text-base font-bold text-neutral-900">{t("cookie.title")}</h2>
            <p className="mt-1 text-sm text-neutral-600">
              {t("cookie.message")}{" "}
              <Link to="/legal/cookies" className="font-medium text-primary underline hover:text-primary/80" data-testid="cookie-policy-link">
                {t("cookie.policy")}
              </Link>
            </p>

            {managing && (
              <div className="mt-3 space-y-3 rounded-lg border border-border bg-secondary/40 p-3" data-testid="cookie-manage-panel">
                <PrefRow title={t("cookie.essential")} desc={t("cookie.essentialDesc")} value locked />
                <PrefRow title={t("cookie.analytics")} desc={t("cookie.analyticsDesc")} value={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testid="cookie-toggle-analytics" />
                <PrefRow title={t("cookie.marketing")} desc={t("cookie.marketingDesc")} value={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testid="cookie-toggle-marketing" />
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {!managing ? (
                <>
                  <Button onClick={() => persist({ analytics: true, marketing: true })} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cookie-accept">
                    {t("cookie.accept")}
                  </Button>
                  <Button variant="outline" onClick={() => persist({ analytics: false, marketing: false })} data-testid="cookie-reject">
                    {t("cookie.reject")}
                  </Button>
                  <Button variant="ghost" onClick={() => setManaging(true)} data-testid="cookie-manage">
                    {t("cookie.manage")}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => persist(prefs)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cookie-save">
                    {t("cookie.save")}
                  </Button>
                  <Button variant="outline" onClick={() => persist({ analytics: true, marketing: true })} data-testid="cookie-accept">
                    {t("cookie.accept")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
